/**
 * ч.1040: «Чек-ап після міграції» (TD-011 / Норма #24) — зауваження для
 * бухгалтера: що у перенесеній базі нестандартне/неповне/потребує рішення.
 * Живий запит GET /migration/checkup (усі правила зараз); те саме зведення
 * на момент перенесення зберігає майстер міграції (reconciliation_report.audit).
 * Нічого не блокує і не змінює.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { CHECKUP_RIGHTS, migrationCheckup } from '@/api/migrationRuns';
import { useAllPermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { DOC_TABLE_NAMES_UK } from '@/utils/moduleNames';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import {
  SEVERITY_LABEL, cellText, passedRules, severityTokens, sortFindings, summaryLine,
  type CheckupFinding, type CheckupSeverity,
} from '@/pages/checkupModel';

function SeverityBadge({ sev }: { sev: CheckupSeverity }) {
  const t = severityTokens(sev);
  return (
    <span style={{
      color: t.fg, background: t.bg, border: `1px solid ${t.bd}`,
      borderRadius: 4, padding: '0 0.4rem', fontSize: '0.75rem', fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      {SEVERITY_LABEL[sev]}
    </span>
  );
}

/** ч.1307: посилання «перейти» несе організацію чек-апу — цільова сторінка відкриється для неї. */
export function linkWithOrg(link: string, orgId: string): string {
  if (!orgId) return link;
  return `${link}${link.includes('?') ? '&' : '?'}org=${encodeURIComponent(orgId)}`;
}

function FindingBlock({ f, orgId }: { f: CheckupFinding; orgId: string }) {
  const [open, setOpen] = useState(f.severity !== 'INFO');
  // Тип документа у деталях — українською назвою списку, не сирим кодом.
  const text = (c: { key: string }, row: Record<string, unknown>) => (
    c.key === 'document_type' && typeof row[c.key] === 'string'
      ? (DOC_TABLE_NAMES_UK[row[c.key] as string] ?? cellText(row[c.key]))
      : cellText(row[c.key]));
  const cols = useMemo<ColumnDef<Record<string, unknown>>[]>(() => f.columns.map((c) => ({
    key: c.key, header: c.label, cell: (row) => text(c, row),
    sortValue: (row) => {
      const v = row[c.key];
      return typeof v === 'number' ? v : Number.isFinite(Number(v)) && v !== '' && v != null
        ? Number(v) : cellText(v);
    },
  })), [f.columns]);
  const t = severityTokens(f.severity);
  return (
    <div style={{
      border: `1px solid ${t.bd}`, borderLeft: `4px solid ${t.bd}`,
      borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '0.75rem',
      background: 'var(--bg-panel)',
    }}>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <SeverityBadge sev={f.severity} />
        <strong>{f.title}</strong>
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>{f.code}</span>
        {f.error == null && (
          <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>
            {f.count.toLocaleString('uk-UA')}
          </span>
        )}
      </div>
      <p style={{ margin: '0.5rem 0 0.25rem' }}>{f.message}</p>
      {f.error ? (
        <p style={{ margin: 0, color: 'var(--muted)', fontSize: '0.85rem' }}>{f.error}</p>
      ) : (
        <>
          {f.action && (
            <p style={{ margin: '0 0 0.5rem', color: 'var(--muted)' }}>
              <span style={{ fontWeight: 600 }}>Що робити: </span>{f.action}
            </p>
          )}
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            {f.link && <Link to={linkWithOrg(f.link, orgId)}>перейти →</Link>}
            {f.rows.length > 0 && (
              <button type="button" className="btn-link" onClick={() => setOpen((v) => !v)}>
                {open ? 'сховати деталі' : `деталі (${f.rows_total ?? f.rows.length})`}
              </button>
            )}
          </div>
          {open && f.rows.length > 0 && (
            <div style={{ marginTop: '0.5rem' }}>
              <DataTable<Record<string, unknown>>
                rows={f.rows} columns={cols}
                rowKey={(row) => JSON.stringify(row)}
                emptyText="—"
              />
              {(f.rows_total ?? 0) > f.rows.length && (
                <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                  показано {f.rows.length} з {f.rows_total}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function MigrationCheckupPage() {
  // ч.1112: сторінку бачить і бухгалтер — право міграції АБО право перегляду чек-апу;
  // `?org=` (з віджета головної) відкриває потрібну організацію.
  const all = useAllPermissionsByOrg();
  const availableOrgs = useMemo(
    () => all.allOrgs.filter((o) => (all.permissionsByOrg[o.id] ?? [])
      .some((p) => (CHECKUP_RIGHTS as readonly string[]).includes(p))),
    [all.allOrgs, all.permissionsByOrg]);
  const permsLoading = all.loading;
  const hasPermissionInAnyOrg = availableOrgs.length > 0;
  const [params] = useSearchParams();
  const [orgId, setOrgId] = useState(params.get('org') ?? '');
  const effectiveOrg = (availableOrgs.some((o) => o.id === orgId) ? orgId : '') || availableOrgs[0]?.id || '';

  const q = useQuery({
    queryKey: ['migration-checkup', effectiveOrg],
    queryFn: () => migrationCheckup.run(effectiveOrg),
    enabled: !!effectiveOrg,
  });
  const report = q.data;
  const findings = useMemo(() => (report ? sortFindings(report.findings) : []), [report]);
  const passed = useMemo(() => (report ? passedRules(report) : []), [report]);

  if (!permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <p>Немає права на міграцію в жодній організації.</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Чек-ап після міграції</h2>
      </div>
      <p style={{ color: 'var(--muted)', margin: '0.25rem 0 0.75rem' }}>
        Сервіс оглядає перенесений облік і показує, що виглядає нестандартно,
        неповно або потребує рішення. Нічого не блокує і не змінює.
      </p>
      {/* ч.1045 (власник): організація окремим рядком, поруч — основна кнопка. */}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <select value={effectiveOrg} onChange={(e) => setOrgId(e.target.value)}>
          {availableOrgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <button type="button" className="primary" onClick={() => q.refetch()}
          disabled={!effectiveOrg || q.isFetching}
          title="Виконати всі перевірки заново для обраної організації (після виправлень)">
          {q.isFetching ? 'перевіряємо…' : 'перевірити ще раз'}
        </button>
      </div>

      {q.isLoading && <p>Перевіряємо…</p>}
      {q.isError && <p style={{ color: 'var(--badge-red-fg)' }}>Не вдалося виконати чек-ап.</p>}

      {report && (
        <>
          <div style={{
            padding: '0.6rem 0.9rem', borderRadius: 6, marginBottom: '1rem',
            background: report.summary.error ? 'var(--badge-red-bg)'
              : report.summary.warn ? 'var(--badge-amber-bg)' : 'var(--badge-green-bg)',
            border: `1px solid ${report.summary.error ? 'var(--badge-red-bd)'
              : report.summary.warn ? 'var(--badge-amber-bd)' : 'var(--badge-green-bd)'}`,
          }}>
            <strong>Підсумок:</strong> {summaryLine(report.summary)}
            <span style={{ color: 'var(--muted)', marginLeft: '0.5rem', fontSize: '0.85rem' }}>
              · перевірок: {report.summary.rules_run} ·{' '}
              {new Date(report.computed_at).toLocaleString('uk-UA')}
            </span>
          </div>

          {findings.map((f) => <FindingBlock key={f.code} f={f} orgId={effectiveOrg} />)}

          {passed.length > 0 && (
            <details style={{ marginTop: '1rem' }}>
              <summary style={{ cursor: 'pointer' }}>
                Перевірено без зауважень: {passed.length}
              </summary>
              <ul style={{ margin: '0.5rem 0 0', color: 'var(--muted)' }}>
                {passed.map((r) => <li key={r.code}>{r.title} <span style={{ fontSize: '0.8rem' }}>({r.code})</span></li>)}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
}
